import React from "react";
import Layout from "antd/es/layout";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Flex from "antd/es/flex";
import Image from "antd/es/image";
import { BrowserRouter } from "react-router-dom";
import Link from "antd/es/typography/Link";
import Title from "antd/es/typography/Title";
import Paragraph from "antd/es/typography/Paragraph";
import List from "antd/es/list";
import message from "antd/es/message";
import Menu from "antd/es/menu";
import Collapse from "antd/es/collapse";
import { ThirdwebProvider } from "thirdweb/react";
import MailOutlined from "@ant-design/icons/MailOutlined";
import MenuOutlined from "@ant-design/icons/MenuOutlined";
import FileTextOutlined from "@ant-design/icons/FileTextOutlined";
import { WalletProvider } from "@tronweb3/tronwallet-adapter-react-hooks";
import { WalletError } from "@tronweb3/tronwallet-abstract-adapter";
import { SolanaContext } from "./components/SolanaContext";
import * as Adapters from "./components/tronWallets";
import { Provider } from "./Provider";
import { Exchange } from "./components/Exchange";
import { Whitepaper } from "./components/Whitepaper";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
            refetchOnWindowFocus: false,
        },
    },
});

const adapters = Object.values(Adapters).map(Adapter => new Adapter());

export const App: React.FunctionComponent = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const onTronError = (error: WalletError) => {
        messageApi.error(error.message);
    };
    return (
        <BrowserRouter>
            {contextHolder}
            <SolanaContext>
                <QueryClientProvider client={queryClient}>
                    <ThirdwebProvider>
                        <Provider>
                            <WalletProvider adapters={adapters} onError={onTronError} autoConnect={false}>
                                <Layout className="Background">
                                    <Layout className="Background__drop">
                                        <Layout.Header className="Header">
                                            <Flex wrap gap="15px" align="center" justify="space-between" className="Header__content">
                                                <Flex wrap gap="15px" align="center">
                                                    <Image preview={false} src={require("./liberstake.png")} width="30px" height="30px" />
                                                    <Title level={1} className="Header__title">
                                                        LiberStake bridge
                                                    </Title>
                                                </Flex>
                                                <Menu
                                                    mode="horizontal"
                                                    theme="dark"
                                                    style={{ backgroundColor: "transparent", borderBottom: "none" }}
                                                    overflowedIndicator={<MenuOutlined />}
                                                    selectable={false}
                                                >
                                                    <Menu.Item key="support" icon={<MailOutlined />}>
                                                        <Link href="mailto:liberstake@gmail.com">Support</Link>
                                                    </Menu.Item>
                                                    <Menu.Item key="whitepaper" icon={<FileTextOutlined />}>
                                                        <Whitepaper />
                                                    </Menu.Item>
                                                </Menu>
                                            </Flex>
                                        </Layout.Header>
                                        <Layout.Content className="Content">
                                            <div className="Content__wrapper">
                                                <Flex vertical gap="15px">
                                                    <Title level={2} className="Content__title">
                                                        LiberStake bridge
                                                    </Title>
                                                    <Exchange />
                                                    <Collapse
                                                        items={[{
                                                            label: "Learn more",
                                                            key: "learn",
                                                            children: (
                                                                <>
                                                                    <Paragraph>
                                                                        The <strong>Liberland Dollar Centralized Exchange</strong> makes it easy to convert LLD between supported blockchains in just 3 simple steps:
                                                                        <List
                                                                            dataSource={[{
                                                                                item: "Choose your source chain and destination chain",
                                                                            }, {
                                                                                item: "Pay using the secure payment gateway",
                                                                            }, {
                                                                                item: "Wait for confirmation — your funds will arrive shortly"
                                                                            }]}
                                                                            renderItem={({ item }) => (
                                                                                <List.Item>
                                                                                    <strong>
                                                                                        {item}
                                                                                    </strong>
                                                                                </List.Item>
                                                                            )}
                                                                        />
                                                                        Fast, user-friendly, and optimized for Liberland’s digital economy.
                                                                    </Paragraph>
                                                                    <Paragraph>
                                                                        Please note: <strong>Transactions are irreversible.</strong> Lost or misdirected funds cannot be recovered.
                                                                    </Paragraph>
                                                                </>
                                                            )
                                                        }]}
                                                    />
                                                </Flex>
                                            </div>
                                        </Layout.Content>
                                        <Layout.Footer className="Footer">
                                            LiberStake app ©{new Date().getFullYear()} Created by Jan Jindráček
                                        </Layout.Footer>
                                    </Layout>
                                </Layout>
                            </WalletProvider>
                        </Provider>
                    </ThirdwebProvider>
                </QueryClientProvider>
            </SolanaContext>
        </BrowserRouter>
    );
};
